'use client';

import { Fragment, useMemo, useState } from 'react';
import type { NotebookFolder } from '@/lib/notebook';
import type { Mode } from '@/lib/api';
import ModeIcon from '@/components/ModeIcons';

/**
 * The sidebar — the part of the notebook that makes it a notebook.
 *
 * ── Why the root is drawn and not stored ────────────────────────────────────
 * "Notebook" at the top is this component's own row. There is no such document
 * (see models/NotebookFolder.ts), which means dropping something on it is a
 * move to `parentId: null` and nothing has to special-case a folder that exists
 * only so the tree has somewhere to start.
 *
 * ── Why the row actions hide ────────────────────────────────────────────────
 * A tree with rename / new / delete printed on every row is a control panel,
 * and this one is meant to be read at a glance to find a place. The three live
 * behind a `⋯` that appears on hover or focus, so the quiet state is a list of
 * names and counts — and the menu is reachable from the keyboard, because a
 * control that only exists under a pointer does not exist for everyone.
 *
 * ── Drag and drop, and the one guard it needs ───────────────────────────────
 * Entries drop on folders to be filed; folders drop on folders to be
 * re-parented. The server refuses a folder dropped inside itself
 * (routes/notebook.ts explains why that particular mistake is unrecoverable
 * from the UI), and this component refuses it a second time so the drop simply
 * does not highlight rather than failing after the release.
 */

export const ENTRY_DRAG = 'application/x-gt-notebook-entry';
export const FOLDER_DRAG = 'application/x-gt-notebook-folder';

export interface TreeNode {
  folder: NotebookFolder;
  children: TreeNode[];
}

/**
 * The icon a folder wears — the mode's own, for the folders the modes made.
 *
 * The six capture folders are the six buttons on the rail, so they are drawn
 * with the six marks from the rail: the arrow on Translate, the gem on AI
 * Prompt. It is the same recognition the column is built on, reused where it
 * costs nothing, and it is what makes the seeded folders read as part of the
 * product rather than as six folders somebody happened to create with those
 * names. Null for every other folder, which then gets the folder glyph.
 *
 * Exported because the destination picker draws these rows too, and a list
 * where Translate is a folder while the sidebar beside it shows an arrow reads
 * as two different trees.
 */
export function folderIcon(folder: NotebookFolder, modes: Mode[]): string | null {
  if (folder.kind !== 'mode' || !folder.mode) return null;
  return modes.find((m) => m.id === folder.mode)?.icon ?? null;
}

/**
 * Folders → THREE GROUPS, in the order the server sent them within each: the
 * six the modes made, the ones the user made, and History.
 *
 * ── Why groups and not one list with the ends pinned ────────────────────────
 * They were one list with hairlines, and a hairline is not enough: a folder
 * called "Client notes" sitting in the same column, at the same indent, in the
 * same type as "AI Prompt" reads as the same kind of thing, and it is not. One
 * of them fills itself and cannot be deleted; the other is the user's and does
 * neither. The sidebar now says so out loud — each group is a titled section —
 * so the answer to "why can't I delete this one" is on screen before the
 * question is asked.
 *
 * ── Why the modes are on top ────────────────────────────────────────────────
 * They are the rail, in the rail's order, and the rail is the thing the reader
 * already knows the shape of. Putting them where the buttons are — a fixed
 * block at the top that never moves — means the tree has a landmark, and the
 * folders somebody made are read against it rather than shuffled through it.
 * They are also the folders that fill themselves, so the first thing under the
 * cursor is the place today's work actually went.
 *
 * ── Why it moved down there ─────────────────────────────────────────────────
 * It was first, on the reasoning that it is the folder with everything in it.
 * That is exactly why it belongs last. History is the one folder nobody has to
 * think about: it fills itself, it is never chosen as a destination, and it is
 * the only one that cannot be renamed, moved or deleted. Sitting at the top it
 * pushed the folders somebody actually made — the ones they are working out of
 * — down the list behind a row that never changes, and it took the position the
 * eye lands on first.
 *
 * It stays at the ROOT, so dragging into it still means what it always did.
 * Only its place among its siblings changes.
 *
 * ── Why the positions are decided here and not by `order` ───────────────────
 * Because `order` is a number in the database and every folder already written
 * has the one it was seeded with — including thirty accounts' worth of user
 * folders numbered from 1, which the mode folders would otherwise interleave
 * with. Pinning at render makes the rule true for every account at once, with
 * no migration and nothing to keep in step. It is also simply where the rule
 * belongs: "the folders that fill themselves sit at the ends" is a statement
 * about the sidebar, not about a row's sort key.
 *
 * Folders the user made keep the server's order untouched, which is what keeps
 * dragging and reordering doing what they look like they do.
 */
export function treeGroups(folders: NotebookFolder[]): {
  modes: TreeNode[];
  own: TreeNode[];
  history: TreeNode[];
} {
  const nodes = new Map<string, TreeNode>();
  for (const f of folders) nodes.set(f.id, { folder: f, children: [] });
  const roots: TreeNode[] = [];
  for (const f of folders) {
    const node = nodes.get(f.id)!;
    const parent = f.parentId ? nodes.get(f.parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  // A stable partition rather than a sort: `filter` preserves the order within
  // each group, so the mode folders keep the rail's order and the folders
  // somebody made stay in exactly the order the server sent them.
  return {
    modes: roots.filter((n) => n.folder.kind === 'mode'),
    own: roots.filter((n) => n.folder.kind === 'normal' || !n.folder.kind),
    history: roots.filter((n) => n.folder.kind === 'history'),
  };
}

/** The three groups as one list — what the rail and the picker walk. */
export function buildTree(folders: NotebookFolder[]): TreeNode[] {
  const g = treeGroups(folders);
  return [...g.modes, ...g.own, ...g.history];
}

/** Every folder id under `id`, itself included — the drop guard's answer. */
function subtree(node: TreeNode): string[] {
  return [node.folder.id, ...node.children.flatMap(subtree)];
}

export default function FolderTree({
  folders,
  counts,
  selected,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onMoveFolder,
  onDropEntry,
  collapsed,
  onToggleCollapsed,
  busy,
  modes,
  readOnly = false,
}: {
  folders: NotebookFolder[];
  counts: Record<string, number>;
  selected: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (name: string, parentId: string | null) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (folder: NotebookFolder) => void;
  onMoveFolder: (id: string, parentId: string | null) => void;
  onDropEntry: (entryId: string, folderId: string) => void;
  /**
   * Squeezed down to a rail of glyphs.
   *
   * The sidebar is the smallest of the three columns and the least often used —
   * most of a session is spent in one folder — so it is the one that gives its
   * width back to the exchange, which is the thing being read. It is not
   * HIDDEN: a sidebar that vanishes takes the reader's sense of where they are
   * with it, and putting it back becomes a hunt for the control that does. The
   * rail keeps every folder on screen, keeps the open one lit, and keeps all of
   * them as drop targets, so dragging an entry into a folder still works with
   * the sidebar shut.
   */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  busy: boolean;
  /** The rail's modes, for the icons the capture folders wear. */
  modes: Mode[];
  /**
   * Somebody else's tree, in the admin console.
   *
   * Everything that writes goes: the row menus, the rename inputs, the drag
   * handles and the "New folder" button. What stays is everything that reads —
   * selecting a folder, opening and closing a branch, the counts, the rail —
   * because a support view has to be able to look everywhere without being able
   * to touch anything.
   */
  readOnly?: boolean;
}) {
  const groups = useMemo(() => treeGroups(folders), [folders]);
  const tree = useMemo(
    () => [...groups.modes, ...groups.own, ...groups.history],
    [groups],
  );
  /** The same tree as one column, for the rail. Hoisted so the map is not O(n²). */
  const rail = useMemo(() => flatten(tree), [tree]);

  /*
   * Which folders are open.
   *
   * Everything starts open. A notebook that greets you with collapsed folders
   * is asking you to remember what is in them, and the whole point of the
   * sidebar is that you do not have to — it is small enough to show whole, and
   * anybody with a big tree can close what they are not using.
   */
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [renaming, setRenaming] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null | undefined>(undefined);
  const [dropTarget, setDropTarget] = useState<string | null | undefined>(undefined);
  const [dragging, setDragging] = useState<string | null>(null);

  const toggle = (id: string) =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Can what is being dragged be dropped on this folder? */
  const canDrop = (types: readonly string[], target: string | null): boolean => {
    // Nothing is droppable in a view that cannot write. Nothing in it is
    // draggable either, so this only catches a drag begun in another window.
    if (readOnly) return false;
    if (types.includes(ENTRY_DRAG)) return target !== null;
    if (types.includes(FOLDER_DRAG)) {
      if (!dragging) return true; // another window's drag — let the server rule
      const node = findNode(tree, dragging);
      if (node && target && subtree(node).includes(target)) return false;
      return true;
    }
    return false;
  };

  const handleDrop = (e: React.DragEvent, target: string | null) => {
    e.preventDefault();
    setDropTarget(undefined);
    const entry = e.dataTransfer.getData(ENTRY_DRAG);
    if (entry) {
      if (target) onDropEntry(entry, target);
      return;
    }
    const folder = e.dataTransfer.getData(FOLDER_DRAG);
    if (folder && folder !== target) onMoveFolder(folder, target);
  };

  const row = (node: TreeNode, depth: number) => {
    const { folder } = node;
    const isHistory = folder.kind === 'history';
    /*
     * The seeded folders stay where they are.
     *
     * History always did; the mode folders join it because capture files into
     * them by mode id and the shelf they form is what the tree is read against.
     * The server refuses the move as well (routes/notebook.ts) — this is what
     * stops the drag from starting, so the refusal is never a surprise at the
     * end of one.
     */
    const locked = isHistory || folder.kind === 'mode' || readOnly;
    const open = !closed.has(folder.id);
    const on = selected === folder.id;
    const over = dropTarget === folder.id;
    return (
      <li key={folder.id}>
        <div
          className={
            'group relative flex items-center gap-1 rounded-lg pr-1 transition-colors ' +
            (on
              ? 'bg-accent-600/10 text-accent-700 dark:bg-accent-400/15 dark:text-accent-200'
              : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800') +
            (over ? ' ring-2 ring-accent-500 ring-offset-0' : '')
          }
          style={{ paddingLeft: 4 + depth * 14 }}
          draggable={!locked && renaming !== folder.id}
          onDragStart={(e) => {
            e.dataTransfer.setData(FOLDER_DRAG, folder.id);
            e.dataTransfer.effectAllowed = 'move';
            setDragging(folder.id);
          }}
          onDragEnd={() => { setDragging(null); setDropTarget(undefined); }}
          onDragOver={(e) => {
            if (!canDrop(e.dataTransfer.types, folder.id)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropTarget(folder.id);
          }}
          onDragLeave={() => setDropTarget((t) => (t === folder.id ? undefined : t))}
          onDrop={(e) => handleDrop(e, folder.id)}
        >
          <button
            type="button"
            onClick={() => toggle(folder.id)}
            aria-label={open ? 'Collapse' : 'Expand'}
            className={
              'flex h-5 w-5 shrink-0 items-center justify-center rounded text-ink-400 ' +
              (node.children.length ? 'hover:text-ink-700 dark:hover:text-ink-100' : 'invisible')
            }
          >
            <svg viewBox="0 0 24 24" className={'h-3 w-3 transition-transform ' + (open ? 'rotate-90' : '')} fill="currentColor">
              <path d="M9 5l7 7-7 7z" />
            </svg>
          </button>

          {renaming === folder.id ? (
            <input
              autoFocus
              defaultValue={folder.name}
              onBlur={(e) => { onRename(folder.id, e.target.value); setRenaming(null); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { onRename(folder.id, e.currentTarget.value); setRenaming(null); }
                /*
                 * `stopPropagation`, and it is the whole point of the branch.
                 *
                 * The dialog closes on Escape, on a listener at the document.
                 * Without this, abandoning a rename would take the notebook down
                 * with it — the reader loses their place and the folder they were
                 * in over a keystroke that meant "never mind about this word".
                 * React's synthetic stopPropagation calls the native one, so the
                 * document listener never sees the key.
                 */
                if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null); }
              }}
              className="my-0.5 w-full rounded border border-accent-500 bg-white px-1.5 py-1 text-[13px]
                         outline-none dark:bg-ink-900 dark:text-ink-50"
            />
          ) : (
            <>
              <button
                type="button"
                onClick={() => onSelect(folder.id)}
                className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left text-[13px] font-medium"
              >
                <FolderGlyph
                  history={isHistory}
                  open={open && node.children.length > 0}
                  icon={folderIcon(folder, modes)}
                  label={folder.name}
                />
                <span className="truncate">{folder.name}</span>
              </button>
              <span className="shrink-0 tabular-nums text-[11px] text-ink-400">
                {counts[folder.id] || ''}
              </span>
              {!readOnly && <RowMenu
                folder={folder}
                onAdd={() => { setAdding(folder.id); setClosed((p) => { const n = new Set(p); n.delete(folder.id); return n; }); }}
                onRename={() => setRenaming(folder.id)}
                onDelete={() => onDelete(folder)}
                busy={busy}
              />}
            </>
          )}
        </div>

        {(open || adding === folder.id) && (
          <ul>
            {adding === folder.id && (
              <NewFolderInput
                depth={depth + 1}
                onDone={(name) => { if (name) onCreate(name, folder.id); setAdding(undefined); }}
              />
            )}
            {open && node.children.map((c) => row(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  /*
   * The root, as a drop target rather than as a row.
   *
   * "Notebook" is not a document (see the file note), so dropping on it means
   * `parentId: null`. It doubles as the sidebar's own header, which is what
   * gives the collapse button somewhere to live that is not stealing a line
   * from the tree.
   */
  const rootDrop = {
    onDragOver: (e: React.DragEvent) => {
      if (!canDrop(e.dataTransfer.types, null)) return;
      e.preventDefault();
      setDropTarget(null);
    },
    onDragLeave: () => setDropTarget((t: string | null | undefined) => (t === null ? undefined : t)),
    onDrop: (e: React.DragEvent) => handleDrop(e, null),
  };

  if (collapsed) {
    /*
     * ── THE RAIL ────────────────────────────────────────────────────────────
     *
     * The same folders, flattened to one column of glyphs. Flattened rather
     * than indented because 52px has no room to say "inside": the nesting is
     * carried by the tooltip, which names the whole path, and by the tree the
     * reader gets back with one click.
     *
     * Every glyph is still a drop target, so an entry can be dragged into a
     * folder without opening the sidebar at all — which is most of what the
     * sidebar was being kept open for.
     */
    return (
      <div className="flex h-full min-h-0 flex-col items-center">
        <div
          className={
            'flex w-full shrink-0 justify-center border-b border-ink-200 p-2 transition-colors ' +
            'dark:border-ink-700 ' +
            (dropTarget === null ? 'bg-accent-600/10' : '')
          }
          {...rootDrop}
        >
          <button
            type="button"
            onClick={onToggleCollapsed}
            title="Show the folders"
            aria-label="Show the folders"
            aria-expanded={false}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-500 transition
                       hover:bg-ink-100 hover:text-ink-900 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-ink-50"
          >
            <Chevron dir="right" />
          </button>
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col items-center gap-1 overflow-y-auto p-2">
          {rail.map(({ folder, path }, i) => {
            const on = selected === folder.id;
            const over = dropTarget === folder.id;
            const n = counts[folder.id] || 0;
            // The same pin, said the same way — see `pinned` in the tree above.
            /* The tree's two hairlines, said the same way — see `pinned` and
               `afterModes` in the rows above. */
            const pinned =
              i > 0 &&
              (folder.kind === 'history' ||
                (folder.kind !== 'mode' && rail[i - 1]?.folder.kind === 'mode'));
            return (
              <Fragment key={folder.id}>
              {/* A rule of its own rather than a border on the button: the
                  button is a 36px square in a centred column, and a border with
                  padding would make the pinned one a different size from every
                  other glyph. It is also the only way to get the tree's own
                  hairline colour in both themes — `currentColor` here is the
                  button's text, which is the accent when it is selected. */}
              {pinned && <span aria-hidden className="my-1 h-px w-5 shrink-0 bg-ink-200 dark:bg-ink-700" />}
              <button
                type="button"
                onClick={() => onSelect(folder.id)}
                title={`${path}${n ? ` — ${n}` : ''}`}
                aria-label={path}
                aria-current={on ? 'true' : undefined}
                draggable={folder.kind === 'normal' && !readOnly}
                onDragStart={(e) => {
                  e.dataTransfer.setData(FOLDER_DRAG, folder.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setDragging(folder.id);
                }}
                onDragEnd={() => { setDragging(null); setDropTarget(undefined); }}
                onDragOver={(e) => {
                  if (!canDrop(e.dataTransfer.types, folder.id)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropTarget(folder.id);
                }}
                onDragLeave={() => setDropTarget((t) => (t === folder.id ? undefined : t))}
                onDrop={(e) => handleDrop(e, folder.id)}
                className={
                  'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ' +
                  (on
                    ? 'bg-accent-600/15 text-accent-700 dark:bg-accent-400/20 dark:text-accent-200'
                    : 'text-ink-500 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800') +
                  (over ? ' ring-2 ring-accent-500' : '')
                }
              >
                <FolderGlyph
                  history={folder.kind === 'history'}
                  open={false}
                  icon={folderIcon(folder, modes)}
                  label={folder.name}
                />
                {/* The count, as a dot with a number in it. The rail has no room
                    for the column the open sidebar puts it in, and dropping it
                    entirely would make an empty folder and a full one look the
                    same — which is the one thing the sidebar is read for. */}
                {n > 0 && (
                  <span
                    aria-hidden
                    className="absolute -right-0.5 -top-0.5 min-w-[15px] rounded-full bg-ink-200 px-1
                               text-center text-[9px] font-semibold leading-[15px] text-ink-600
                               dark:bg-ink-700 dark:text-ink-200"
                  >
                    {n > 99 ? '99+' : n}
                  </span>
                )}
              </button>
              </Fragment>
            );
          })}
        </div>

        {!readOnly && (
        <div className="w-full shrink-0 border-t border-ink-200 p-2 dark:border-ink-700">
          <button
            type="button"
            onClick={onToggleCollapsed}
            disabled={busy}
            title="New folder"
            aria-label="New folder"
            className="flex h-9 w-full items-center justify-center rounded-lg text-ink-500 transition
                       hover:bg-ink-100 hover:text-accent-600 disabled:opacity-50
                       dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-accent-300"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The header: the root drop target on the left, the collapse on the right. */}
      <div
        className={
          'flex shrink-0 items-center gap-1.5 border-b border-ink-200 px-3 py-2 transition-colors ' +
          'dark:border-ink-700 ' +
          (dropTarget === null ? 'bg-accent-600/10 ring-1 ring-inset ring-accent-500' : '')
        }
        {...rootDrop}
      >
        <span className="flex-1 truncate text-[11px] font-semibold uppercase tracking-wider text-ink-400">
          Notebook
        </span>
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Hide the folders"
          aria-label="Hide the folders"
          aria-expanded
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-ink-400 transition
                     hover:bg-ink-200 hover:text-ink-800 dark:hover:bg-ink-700 dark:hover:text-ink-50"
        >
          <Chevron dir="left" />
        </button>
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto p-2">
        {/*
          Three sections, in the order the notebook fills them.

          "Modes" first because that is where today's work went and it is the
          block that never changes; the user's own folders next, which is the
          part that grows; History last, on its own, because it is the one
          nobody has to think about. Each is titled, so the difference between a
          folder that files itself and one somebody made is on the screen rather
          than in the difference between two icons.
        */}
        {/* "Modes" and "Recorded" are the SYSTEM's view of a notebook — the
            automatic per-mode capture and the whole-notebook history. They only
            appear where the whole record is meant to be seen: the admin console's
            read-only view, whose source returns them. A person's own notebook
            returns only the folders they made, so both sections are simply empty
            there and never drawn, leaving just "Your folders". */}
        {groups.modes.length > 0 && (
          <Group label="Modes" hint="One per button on the rail. They fill themselves." first>
            {groups.modes.map((n) => row(n, 0))}
          </Group>
        )}

        <Group label="Your folders" hint="Anything you make. Drag entries in." first={groups.modes.length === 0}>
          {!readOnly && adding === null && (
            <NewFolderInput depth={0} onDone={(name) => { if (name) onCreate(name, null); setAdding(undefined); }} />
          )}
          {groups.own.length === 0 && adding !== null && (
            /* Not an error and not a call to action — the "New folder" button is
               already pinned at the bottom of this column. One quiet line, so
               the empty section reads as empty rather than as broken. */
            <li className="px-2 py-1.5 text-[12px] text-ink-400">Nothing here yet.</li>
          )}
          {groups.own.map((n) => row(n, 0))}
        </Group>

        {groups.history.length > 0 && (
          <Group label="Recorded" hint="Everything you have translated, whichever folder it is filed in.">
            {groups.history.map((n) => row(n, 0))}
          </Group>
        )}
      </ul>

      {!readOnly && (
      <div className="shrink-0 border-t border-ink-200 p-2 dark:border-ink-700">
        <button
          type="button"
          onClick={() => setAdding(null)}
          disabled={busy}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-[13px] font-medium
                     text-ink-600 transition hover:bg-ink-100 hover:text-accent-600
                     disabled:opacity-50 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-accent-300"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New folder
        </button>
      </div>
      )}
    </div>
  );
}

/**
 * A titled block of the tree.
 *
 * The heading is a `<p>` inside an `<li>` rather than a row of its own, so the
 * list stays a list to a screen reader and the title is read as what it is —
 * the name of the group, not a folder nobody can open. The rule above it is the
 * separator; the first group has none, because a line at the very top of a
 * column divides the header from the list rather than one group from another.
 *
 * `hint` is a title attribute rather than a second line of text: the sidebar is
 * 250px wide and a sentence under each heading would cost three rows of tree to
 * say what the heading already says.
 */
function Group({
  label,
  hint,
  first,
  children,
}: {
  label: string;
  hint: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className={first ? '' : 'mt-2.5 border-t border-ink-200 pt-2 dark:border-ink-700'}>
      <p
        title={hint}
        className="cursor-default px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400"
      >
        {label}
      </p>
      <ul>{children}</ul>
    </li>
  );
}

/**
 * The tree as one list, each folder carrying the path that names it.
 *
 * Only the rail uses this. The path — "Work / Client A" — is what its tooltip
 * says, because a glyph on its own cannot show nesting and "Client A" alone is
 * ambiguous the moment somebody has two of them.
 */
function flatten(nodes: TreeNode[], prefix = ''): { folder: NotebookFolder; path: string }[] {
  return nodes.flatMap((n) => {
    const path = prefix ? `${prefix} / ${n.folder.name}` : n.folder.name;
    return [{ folder: n.folder, path }, ...flatten(n.children, path)];
  });
}

/** The one glyph the collapse button uses, pointed either way. */
function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={dir === 'left' ? 'M14 6l-6 6 6 6' : 'M10 6l6 6-6 6'} />
    </svg>
  );
}

function findNode(nodes: TreeNode[], id: string): TreeNode | null {
  for (const n of nodes) {
    if (n.folder.id === id) return n;
    const hit = findNode(n.children, id);
    if (hit) return hit;
  }
  return null;
}

/** The inline "name it" row. Escape or an empty name abandons it. */
function NewFolderInput({ depth, onDone }: { depth: number; onDone: (name: string) => void }) {
  return (
    <li style={{ paddingLeft: 4 + depth * 14 + 20 }}>
      <input
        autoFocus
        placeholder="Folder name"
        onBlur={(e) => onDone(e.target.value.trim())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onDone(e.currentTarget.value.trim());
          // Escape abandons naming the folder and nothing else — see the note on
          // the rename input above for why it must not reach the document.
          if (e.key === 'Escape') { e.stopPropagation(); onDone(''); }
        }}
        className="my-0.5 w-full rounded border border-accent-500 bg-white px-1.5 py-1 text-[13px]
                   outline-none dark:bg-ink-900 dark:text-ink-50"
      />
    </li>
  );
}

/**
 * The `⋯` and what is behind it.
 *
 * The seeded folders offer less, because they refuse more. History gets "new
 * sub-folder" and nothing else; a mode folder gets that and Rename — the name
 * is only a label, and capture finds it by mode id — but not Delete, because
 * that mode's record would have nowhere to go. An item that is present and
 * refuses is worse than one that is not offered, so neither is drawn, and a
 * line in the menu says why rather than leaving somebody hunting for the action
 * they remember being there.
 */
function RowMenu({
  folder,
  onAdd,
  onRename,
  onDelete,
  busy,
}: {
  folder: NotebookFolder;
  onAdd: () => void;
  onRename: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isHistory = folder.kind === 'history';
  const isMode = folder.kind === 'mode';

  return (
    <span className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Actions for ${folder.name}`}
        aria-expanded={open}
        disabled={busy}
        className={
          'flex h-6 w-6 items-center justify-center rounded text-ink-400 transition ' +
          'hover:bg-ink-200 hover:text-ink-700 focus:opacity-100 dark:hover:bg-ink-700 dark:hover:text-ink-50 ' +
          (open ? '' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100')
        }
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
          <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>

      {open && (
        <>
          {/* The click-away. A menu that only closes on its own items is a menu
              you have to use to get rid of. */}
          <span className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <span
            className="absolute right-0 top-7 z-20 flex w-44 flex-col overflow-hidden rounded-lg border
                       border-ink-200 bg-white py-1 shadow-xl dark:border-ink-700 dark:bg-ink-900"
          >
            <MenuItem label="New sub-folder" onClick={() => { setOpen(false); onAdd(); }} />
            {!isHistory && <MenuItem label="Rename" onClick={() => { setOpen(false); onRename(); }} />}
            {!isHistory && !isMode && (
              <MenuItem label="Delete folder" danger onClick={() => { setOpen(false); onDelete(); }} />
            )}
            {(isHistory || isMode) && (
              <span className="px-3 py-2 text-[11px] leading-snug text-ink-400">
                {isMode
                  ? `Everything you translate in ${folder.name} is recorded here, so this folder stays.`
                  : 'Everything you translate is recorded here, so this folder stays.'}
              </span>
            )}
          </span>
        </>
      )}
    </span>
  );
}

function MenuItem({ label, onClick, danger }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-1.5 text-left text-[13px] transition ' +
        (danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10'
          : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800')
      }
    >
      {label}
    </button>
  );
}

/**
 * A folder, the mark of the mode that fills it, or the clock that means "this
 * one fills itself".
 *
 * Exported because the destination picker draws the same rows — a list of
 * folders that used plain text while the sidebar beside it used glyphs read as
 * two different products.
 */
export function FolderGlyph({
  history,
  open,
  icon,
  label,
}: {
  history: boolean;
  open: boolean;
  /** A mode's icon name, for a capture folder. See `folderIcon`. */
  icon?: string | null;
  label?: string;
}) {
  if (icon) {
    /*
     * The rail's own artwork, at the sidebar's size.
     *
     * The mode icons are drawn at 18px for the column and these rows are 16px,
     * so the child selector re-sizes the SVG rather than the file shipping a
     * second copy of ten icons. `text-ink-400` matches the folder glyph beside
     * it, and the two icons that carry their own colour (the gem, the notebook)
     * keep it, which is the point of them.
     */
    return (
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-ink-400 [&>svg]:h-4 [&>svg]:w-4">
        <ModeIcon name={icon} label={label ?? ''} />
      </span>
    );
  }
  if (history) {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-accent-600 dark:text-accent-400" fill="none" stroke="currentColor" strokeWidth="1.7">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-ink-400" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
      {open ? (
        <path d="M3.5 9.5V6.5a1 1 0 011-1h4l2 2h8a1 1 0 011 1v1M3.5 9.5h17l-2 8a1 1 0 01-1 .8H4.5a1 1 0 01-1-1v-7.8z" />
      ) : (
        <path d="M3.5 7a1 1 0 011-1h4l2 2h9a1 1 0 011 1v8.5a1 1 0 01-1 1h-15a1 1 0 01-1-1V7z" />
      )}
    </svg>
  );
}
